import React from 'react';
import { get } from 'lodash';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { HomeModulesModel } from 'client/data/models/home-modules';
import { PageModel } from 'client/data/models/page';
import { CmsModel, CmsPaths } from 'client/data/models/cms';
import { buildVehicleDataPath, VehicleEditorialModel } from 'client/data/models/vehicle-editorial';
import { buildMakeModelDefaultYear, VehicleDefaultModel } from 'client/data/models/vehicle-v2-default';
import { getTreehouseEligiblePath, TreehouseModel } from 'client/data/models/treehouse';
import { getPageLinksPath, LinkWidgetsModel } from 'client/data/models/link-widgets';
import { buildMakeModelInventoryCountPath, InventoryAggregateModel } from 'client/data/models/inventory-aggregate';
import {
  RESEARCH_CATEGORY_EDITORIAL_PATH,
  RESEARCH_CUSTOM_ADS_PATH,
  CAR_NEWS_CATEGORY_EDITORIAL_PATH,
  RESEARCH_LINK_WIDGET_EDITORIAL_PATH,
} from 'site-modules/shared/constants/editorial/research-article';
import { CAR_NEWS_LANDING_CONTENT_PATH } from 'site-modules/shared/constants/editorial/editorial';
import { getResearchArticleIndexPath } from 'site-modules/editorial/utils/content-path';
import { handle404Error } from 'site-modules/shared/utils/errors';
import { isBestContent } from 'site-modules/editorial/utils/best';
import { getBestKeys } from 'site-modules/editorial/utils/article-critical-css-keys';
import { legacyPageUpdate } from 'client/actions/legacy-page';
import { updateVehicle } from 'client/actions/vehicle';
import { setCriticalCss } from 'client/actions/critical-css';
import { parseContent } from 'client/data/cms/content';
import { isTitleEvenCount } from 'site-modules/editorial/utils/homepage-ads-title-count';
import { getBestSeoObject } from 'site-modules/editorial/pages/best/seo';
import { isCarNews } from 'site-modules/editorial/utils/car-news';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import {
  EV_RANGE_LEADERBOARD_HOSTING_PAGE,
  EV_RANGE_LEADERBOARD_CONTENT_PATH,
} from 'site-modules/shared/constants/ev-range-tables';
import { DEALERLESS_OEM } from 'site-modules/shared/constants/oem';
import { getResearchArticlePageName, getCategoryName } from 'site-modules/editorial/utils/research-article';
import { getHeroImageUrl } from 'site-modules/shared/components/editorial/article-hero-photo/article-hero-photo';
import { getHeroPhoto } from 'site-modules/editorial/utils/hero-photo';
import { isEvArticle } from 'site-modules/editorial/utils/ev';
import { isSellCarPage } from 'site-modules/editorial/utils/sell-car';
import { setAdsListToPreload } from 'site-modules/shared/components/native-ad/utils/utils';
import { SEGMENT_LISTING_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { HOME_PAGE_CONTENT_PATH } from 'site-modules/shared/constants/home-page';

import { getSeoObject } from './seo';

export const getPageOptions = ({
  articleCategory,
  articleId,
  splat,
  pageTemplate: PageTemplate,
  chunkName,
  preloadHeroImage,
}) => ({
  name: getResearchArticlePageName(articleCategory),
  category: 'advice',
  chunkName,
  adsCustomTargeting: {
    sect1: articleId.replace(/[^a-zA-Z0-9]/g, ''),
    sect2: articleCategory.replace(/[^a-zA-Z0-9]/g, ''),
  },
  seo: (state, props) => {
    const articlePath = getResearchArticleIndexPath(articleCategory, articleId, splat);
    const content = parseContent(state.cms.content[articlePath]);
    const seoObj = isBestContent(content.id)
      ? getBestSeoObject(content, props.location.pathname)
      : getSeoObject(state, articlePath, getCategoryName(articleCategory), splat);

    return {
      ...seoObj,
    };
  },
  preload: (store, props) => {
    const preloader = new ModelPreloader(store);
    const articlePath = getResearchArticleIndexPath(articleCategory, articleId, splat);
    const pathname = get(props, 'location.pathname', '');
    const { mobile: isMobile } = store.getState();

    preloader.resolve(`content["${articlePath}"]`, CmsModel);

    return preloader.load().then(modelState => {
      const article = modelState.get(`content["${articlePath}"]`, CmsModel);
      const articleContent = parseContent(article);

      if (!(article && article.id)) {
        throw handle404Error({ errorMessage: `No content was found for ${articlePath}` });
      }
      const isBestContentId = isBestContent(article.id);
      const isCarNewsCategory = isCarNews(articleCategory);

      if (isBestContentId) {
        setAdsListToPreload(preloader, store.getState(), [SEGMENT_LISTING_AD.AD_NAME], { type: { niceName: 'suv' } });
        preloader.resolve(`content["${HOME_PAGE_CONTENT_PATH}"]`, CmsModel);
      } else {
        const { title, subtitle } = articleContent;
        if (isCarNewsCategory || isTitleEvenCount(title, subtitle)) {
          preloader.resolve('homeHeroContent', HomeModulesModel);
        }

        if (isEvArticle({ slug: articleCategory, categories: article.categories })) {
          preloader.resolve(getTreehouseEligiblePath(), TreehouseModel);
        }

        preloader.resolve(CmsPaths.buildAllAuthorsPath({ contentPath: articlePath }), CmsModel);
        preloader.resolve(`content["${RESEARCH_LINK_WIDGET_EDITORIAL_PATH}"]`, CmsModel);
        preloader.resolve(`content["${RESEARCH_CATEGORY_EDITORIAL_PATH}"]`, CmsModel);
        preloader.resolve(`content["${CAR_NEWS_CATEGORY_EDITORIAL_PATH}"]`, CmsModel);
        preloader.resolve(`content["${RESEARCH_CUSTOM_ADS_PATH}"]`, CmsModel);
      }

      if (
        isCarNewsCategory ||
        isSellCarPage(articleCategory) ||
        article.id.includes('/car-leasing/monthly-199-lease-deals')
      ) {
        preloader.resolve(getPageLinksPath(), LinkWidgetsModel);
      }

      const contentVehicle = get(articleContent, 'vehicles[0]');
      if (isCarNewsCategory) {
        if (articleContent.hasChild('leaderboard')) {
          store.dispatch(
            legacyPageUpdate({
              pageName: `leaderboard_car_news_article`,
            })
          );
        }

        preloader.resolve(`content["${CAR_NEWS_LANDING_CONTENT_PATH}"]`, CmsModel);

        if (
          !!(contentVehicle?.make && contentVehicle?.model) &&
          !DEALERLESS_OEM.includes(makeNiceName(contentVehicle?.make))
        ) {
          preloader.resolve(
            buildMakeModelInventoryCountPath({
              make: makeNiceName(contentVehicle.make),
              model: makeNiceName(contentVehicle.model),
              radius: 100,
            }),
            InventoryAggregateModel
          );

          preloader.resolve(
            buildMakeModelDefaultYear({
              make: makeNiceName(contentVehicle.make),
              model: makeNiceName(contentVehicle.model),
            }),
            VehicleDefaultModel
          );
        }
      }

      if (pathname === EV_RANGE_LEADERBOARD_HOSTING_PAGE) {
        preloader.resolve(`content["${EV_RANGE_LEADERBOARD_CONTENT_PATH}"]`, CmsModel);
      }

      preloader.resolve(
        buildVehicleDataPath({
          make: makeNiceName(contentVehicle?.make),
          model: makeNiceName(contentVehicle?.model),
          year: contentVehicle?.year,
        }),
        VehicleEditorialModel
      );

      return preloader.load().then(modelStateInstance => {
        const { makeId, modelLinkCode, modelYearId } =
          modelStateInstance.get(
            buildVehicleDataPath({
              make: makeNiceName(contentVehicle?.make),
              model: makeNiceName(contentVehicle?.model),
              year: contentVehicle?.year,
            }),
            VehicleEditorialModel
          ) || {};

        const electricType = isEvArticle({ slug: articleCategory, categories: articleContent.categories })
          ? 'electric'
          : undefined;
        const typeValueFromCms = articleContent.metadata('adBodyType').value() || undefined;

        store.dispatch(
          updateVehicle({
            type: { niceName: typeValueFromCms || electricType },
            make: { niceName: makeNiceName(contentVehicle?.make), id: makeId },
            model: { niceName: makeNiceName(contentVehicle?.model), modelLinkCode },
            modelYear: { year: contentVehicle?.year, id: modelYearId },
          })
        );
        const heroPhoto = getHeroPhoto(articleContent);
        const criticalCssHeight = isMobile ? 1800 : 1400;

        store.dispatch(
          setCriticalCss(
            isBestContentId
              ? {
                  ...getBestKeys(parseContent(article)),
                  criticalCssHeight,
                }
              : {
                  chunkName,
                  hasHeroPhoto: !!heroPhoto,
                  isCarNewsCategory,
                }
          )
        );

        if (preloadHeroImage && heroPhoto) {
          const { href } = heroPhoto;
          const imageLink = getHeroImageUrl({ href, isMobile });

          const preloadHeroImageAsset = {
            href: imageLink,
            as: 'image',
          };

          preloader.set('server.preloadedAssets', PageModel, [preloadHeroImageAsset]);
        }

        return preloader.load();
      });
    });
  },
  component: () => <PageTemplate articleCategory={articleCategory} articleId={articleId} splat={splat} />,
  options: {
    isEditorialPageWithInlineMobileAd: true,
  },
});
